import React, { useState } from "react";
import {
    CalibrationVideo,
    CameraCalibrationAndExtrinsics,
    CameraMatrix,
    DistortionCoefficients,
    Pose
} from "../../types/index";

interface CalibrationPanelProps {
    calibrationVideos: CalibrationVideo[];
    selectedCalibrationVideoId: number | null;
    calibrationData: CameraCalibrationAndExtrinsics;
    onVideoSelect: (videoId: number | null) => void;
    onCalibrationChange: (data: CameraCalibrationAndExtrinsics) => void;
}

const CalibrationPanel: React.FC<CalibrationPanelProps> = ({
    calibrationVideos,
    selectedCalibrationVideoId,
    calibrationData,
    onVideoSelect,
    onCalibrationChange
}) => {
    const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set());

    const toggleSection = (sectionName: string) => {
        const newExpanded = new Set(expandedSections);
        if (newExpanded.has(sectionName)) {
            newExpanded.delete(sectionName);
        } else {
            newExpanded.add(sectionName);
        }
        setExpandedSections(newExpanded);
    };

    const updateCameraMatrix = (field: keyof CameraMatrix, value: number) => {
        if (isNaN(value)) return;

        const newMatrix = new CameraMatrix(calibrationData.camera_matrix);
        (newMatrix as any)[field] = value;
        onCalibrationChange(new CameraCalibrationAndExtrinsics({
            ...calibrationData,
            camera_matrix: newMatrix
        }));
    };

    const updateDistortion = (field: keyof DistortionCoefficients, value: number) => {
        if (isNaN(value)) return;

        const newDistortion = new DistortionCoefficients(calibrationData.distortion_coefficients);
        (newDistortion as any)[field] = value;
        onCalibrationChange(new CameraCalibrationAndExtrinsics({
            ...calibrationData,
            distortion_coefficients: newDistortion
        }));
    };

    const updatePose = (field: keyof Pose, value: number) => {
        if (isNaN(value)) return;

        const newPose = new Pose(calibrationData.pose);
        (newPose as any)[field] = value;
        onCalibrationChange(new CameraCalibrationAndExtrinsics({
            ...calibrationData,
            pose: newPose
        }));
    };

    const updateImageDimensions = (field: 'image_width' | 'image_height', value: number) => {
        if (isNaN(value)) return;

        const updated = new CameraCalibrationAndExtrinsics(calibrationData);
        updated[field] = value;
        onCalibrationChange(updated);
    };

    return (
        <div className="col-md-6">
            <h6 className="mb-3">Camera Calibration</h6>

            {/* Calibration Video Selector */}
            {calibrationVideos.length > 0 && (
                <div className="mb-3">
                    <label htmlFor="calibration_video" className="form-label">
                        Calibration Video
                    </label>
                    <select
                        className="form-select"
                        id="calibration_video"
                        value={selectedCalibrationVideoId || ''}
                        onChange={(e) => onVideoSelect(e.target.value ? parseInt(e.target.value) : null)}
                    >
                        <option value="">None (use default parameters)</option>
                        {calibrationVideos
                            .filter(video => video.status === 'completed')
                            .map(video => (
                                <option key={video.id} value={video.id}>
                                    {video.camera} | {video.title} | {video.image_width}x{video.image_height}
                                    {video.camera_model && ` | ${video.camera_model}`}
                                    {video.camera_mode && ` | ${video.camera_mode}`}
                                </option>
                            ))}
                    </select>
                </div>
            )}

            {/* Camera Matrix */}
            <div className="mb-3">
                <div
                    className="d-flex align-items-center p-2 bg-light border rounded cursor-pointer"
                    onClick={() => toggleSection('camera_matrix')}
                    style={{ cursor: 'pointer' }}
                >
                    <i className={`bi bi-chevron-${expandedSections.has('camera_matrix') ? 'down' : 'right'} me-2`}></i>
                    <h6 className="mb-0">Camera Matrix</h6>
                </div>
                {expandedSections.has('camera_matrix') && (
                    <div className="row g-2 mt-2">
                        <div className="col-6">
                            <label className="form-label small">fx</label>
                            <input
                                type="number"
                                className="form-control form-control-sm"
                                value={calibrationData.camera_matrix.fx}
                                onChange={(e) => updateCameraMatrix('fx', parseFloat(e.target.value))}
                                step="any"
                            />
                        </div>
                        <div className="col-6">
                            <label className="form-label small">fy</label>
                            <input
                                type="number"
                                className="form-control form-control-sm"
                                value={calibrationData.camera_matrix.fy}
                                onChange={(e) => updateCameraMatrix('fy', parseFloat(e.target.value))}
                                step="any"
                            />
                        </div>
                        <div className="col-6">
                            <label className="form-label small">cx</label>
                            <input
                                type="number"
                                className="form-control form-control-sm"
                                value={calibrationData.camera_matrix.cx}
                                onChange={(e) => updateCameraMatrix('cx', parseFloat(e.target.value))}
                                step="any"
                            />
                        </div>
                        <div className="col-6">
                            <label className="form-label small">cy</label>
                            <input
                                type="number"
                                className="form-control form-control-sm"
                                value={calibrationData.camera_matrix.cy}
                                onChange={(e) => updateCameraMatrix('cy', parseFloat(e.target.value))}
                                step="any"
                            />
                        </div>
                        <div className="col-6">
                            <label className="form-label small">skew</label>
                            <input
                                type="number"
                                className="form-control form-control-sm"
                                value={calibrationData.camera_matrix.skew}
                                onChange={(e) => updateCameraMatrix('skew', parseFloat(e.target.value))}
                                step="any"
                            />
                        </div>
                    </div>
                )}
            </div>

            {/* Distortion Coefficients */}
            <div className="mb-3">
                <div
                    className="d-flex align-items-center p-2 bg-light border rounded cursor-pointer"
                    onClick={() => toggleSection('distortion')}
                    style={{ cursor: 'pointer' }}
                >
                    <i className={`bi bi-chevron-${expandedSections.has('distortion') ? 'down' : 'right'} me-2`}></i>
                    <h6 className="mb-0">Distortion Coefficients</h6>
                </div>
                {expandedSections.has('distortion') && (
                    <div className="row g-2 mt-2">
                        <div className="col-6">
                            <label className="form-label small">k1</label>
                            <input
                                type="number"
                                className="form-control form-control-sm"
                                value={calibrationData.distortion_coefficients.k1}
                                onChange={(e) => updateDistortion('k1', parseFloat(e.target.value))}
                                step="any"
                            />
                        </div>
                        <div className="col-6">
                            <label className="form-label small">k2</label>
                            <input
                                type="number"
                                className="form-control form-control-sm"
                                value={calibrationData.distortion_coefficients.k2}
                                onChange={(e) => updateDistortion('k2', parseFloat(e.target.value))}
                                step="any"
                            />
                        </div>
                        <div className="col-6">
                            <label className="form-label small">k3</label>
                            <input
                                type="number"
                                className="form-control form-control-sm"
                                value={calibrationData.distortion_coefficients.k3}
                                onChange={(e) => updateDistortion('k3', parseFloat(e.target.value))}
                                step="any"
                            />
                        </div>
                        <div className="col-6">
                            <label className="form-label small">p1</label>
                            <input
                                type="number"
                                className="form-control form-control-sm"
                                value={calibrationData.distortion_coefficients.p1}
                                onChange={(e) => updateDistortion('p1', parseFloat(e.target.value))}
                                step="any"
                            />
                        </div>
                        <div className="col-6">
                            <label className="form-label small">p2</label>
                            <input
                                type="number"
                                className="form-control form-control-sm"
                                value={calibrationData.distortion_coefficients.p2}
                                onChange={(e) => updateDistortion('p2', parseFloat(e.target.value))}
                                step="any"
                            />
                        </div>
                    </div>
                )}
            </div>

            {/* Pose */}
            <div className="mb-3">
                <div
                    className="d-flex align-items-center p-2 bg-light border rounded cursor-pointer"
                    onClick={() => toggleSection('pose')}
                    style={{ cursor: 'pointer' }}
                >
                    <i className={`bi bi-chevron-${expandedSections.has('pose') ? 'down' : 'right'} me-2`}></i>
                    <h6 className="mb-0">Pose</h6>
                </div>
                {expandedSections.has('pose') && (
                    <div className="row g-2 mt-2">
                        <div className="col-4">
                            <label className="form-label small">x</label>
                            <input
                                type="number"
                                className="form-control form-control-sm"
                                value={calibrationData.pose.x}
                                onChange={(e) => updatePose('x', parseFloat(e.target.value))}
                                step="any"
                            />
                        </div>
                        <div className="col-4">
                            <label className="form-label small">y</label>
                            <input
                                type="number"
                                className="form-control form-control-sm"
                                value={calibrationData.pose.y}
                                onChange={(e) => updatePose('y', parseFloat(e.target.value))}
                                step="any"
                            />
                        </div>
                        <div className="col-4">
                            <label className="form-label small">z</label>
                            <input
                                type="number"
                                className="form-control form-control-sm"
                                value={calibrationData.pose.z}
                                onChange={(e) => updatePose('z', parseFloat(e.target.value))}
                                step="any"
                            />
                        </div>
                        <div className="col-4">
                            <label className="form-label small">roll</label>
                            <input
                                type="number"
                                className="form-control form-control-sm"
                                value={calibrationData.pose.roll}
                                onChange={(e) => updatePose('roll', parseFloat(e.target.value))}
                                step="any"
                            />
                        </div>
                        <div className="col-4">
                            <label className="form-label small">pitch</label>
                            <input
                                type="number"
                                className="form-control form-control-sm"
                                value={calibrationData.pose.pitch}
                                onChange={(e) => updatePose('pitch', parseFloat(e.target.value))}
                                step="any"
                            />
                        </div>
                        <div className="col-4">
                            <label className="form-label small">yaw</label>
                            <input
                                type="number"
                                className="form-control form-control-sm"
                                value={calibrationData.pose.yaw}
                                onChange={(e) => updatePose('yaw', parseFloat(e.target.value))}
                                step="any"
                            />
                        </div>
                    </div>
                )}
            </div>

            {/* Image Dimensions */}
            <div className="mb-3">
                <div
                    className="d-flex align-items-center p-2 bg-light border rounded cursor-pointer"
                    onClick={() => toggleSection('dimensions')}
                    style={{ cursor: 'pointer' }}
                >
                    <i className={`bi bi-chevron-${expandedSections.has('dimensions') ? 'down' : 'right'} me-2`}></i>
                    <h6 className="mb-0">Image Dimensions</h6>
                </div>
                {expandedSections.has('dimensions') && (
                    <div className="row g-2 mt-2">
                        <div className="col-6">
                            <label className="form-label small">Width</label>
                            <input
                                type="number"
                                className="form-control form-control-sm"
                                value={calibrationData.image_width}
                                onChange={(e) => updateImageDimensions('image_width', parseInt(e.target.value))}
                                step="1"
                            />
                        </div>
                        <div className="col-6">
                            <label className="form-label small">Height</label>
                            <input
                                type="number"
                                className="form-control form-control-sm"
                                value={calibrationData.image_height}
                                onChange={(e) => updateImageDimensions('image_height', parseInt(e.target.value))}
                                step="1"
                            />
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default CalibrationPanel;
